import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  Container,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api, { handleUnauthorized } from '../../services/api';
import ApprovedProgressList from './ApprovedProgressList';
import { useAuth } from '../../context/AuthContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { format } from 'date-fns';
import { t } from '../../utils/textUtils';

const ApprovalProgress = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  
  // Active tab: 0 = pending approval, 1 = already approved
  const [activeTab, setActiveTab] = useState(0);

  // State for project selection
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  
  // State for pending submissions
  const [pendingSubmissions, setPendingSubmissions] = useState([]);

  // Filter state
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [submitterFilter, setSubmitterFilter] = useState('all');
  
  // State for loading and error handling
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for rejection dialog
  const [rejectionDialog, setRejectionDialog] = useState({
    open: false,
    submissionId: null,
    reason: ''
  });
  
  // State for image preview dialog
  const [imagePreview, setImagePreview] = useState({
    open: false,
    url: '',
    caption: ''
  });
  
  // State for submission detail dialog
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    submission: null
  });
  
  // Task status is now computed automatically from volume progress (no manual edit)
  
  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await api.get('/users/me/projects');
        // Ensure projects is always an array
        if (response.data && Array.isArray(response.data)) {
          setProjects(response.data);
          console.log(`Loaded ${response.data.length} projects from /users/me/projects`);
          
          // Default to showing all projects (empty projectId)
          // Use setTimeout to ensure the UI has time to render properly before setting the value
          setTimeout(() => {
            setProjectId("");
            console.log("Default filter set to 'SEMUA PROYEK'");
          }, 100);
        } else {
          console.error('Invalid projects data format:', response.data);
          setProjects([]);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(t('GAGAL MEMUAT PROYEK. SILAKAN COBA LAGI.'));
        setProjects([]);
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  // Generic function to fetch pending submissions with optional project filter
  const fetchSubmissions = useCallback(async (specificProjectId = null) => {
    try {
      setLoading(true);
      let url = '/progress-submissions/pending';
      
      // If a specific project ID is provided, filter by that project
      if (specificProjectId) {
        url += `?projectId=${specificProjectId}`;
        console.log(`Fetching pending submissions for project ID: ${specificProjectId}`);
      } else {
        // Otherwise, get all projects and fetch submissions for all of them
        const projectsResponse = await api.get('/users/me/projects');
        
        if (Array.isArray(projectsResponse.data) && projectsResponse.data.length > 0) {
          const projectIds = projectsResponse.data.map(p => p.id).join(',');
          url += `?projectIds=${projectIds}`;
          console.log(`Fetching pending submissions for all projects: ${projectIds}`);
        }
      }
      
      // Make the API call to get submissions
      const response = await api.get(url);
      
      if (Array.isArray(response.data)) {
        // If filtering by a specific project, double check on the client side
        let filteredSubmissions = response.data;
        
        if (specificProjectId) {
          filteredSubmissions = response.data.filter(
            submission => submission.projectId === parseInt(specificProjectId)
          );
        }
        
        const computeProjectedStatus = (submission) => {
          const quantity = parseFloat(submission?.quantity || 0);
          const completed = parseFloat(submission?.completedQuantity || 0);
          const submittedVolume = parseFloat(submission?.volume || 0);
          const totalDone = completed + submittedVolume;

          if (Number.isFinite(quantity) && quantity > 0) {
            if (totalDone >= quantity) return 'done';
            if (totalDone > 0) return 'partial';
            return 'to_do';
          }

          const rawStatus = (submission?.taskStatus || '').toLowerCase();
          if (rawStatus === 'in_progress') return 'partial';
          return rawStatus || 'to_do';
        };

        // Use projected status for pending submissions (includes current + submitted volume)
        const submissionsWithStatus = filteredSubmissions.map(submission => {
          // Debug the submission data to see what fields are available
          console.log('Submission data:', {
            id: submission.id,
            taskId: submission.taskId,
            taskName: submission.taskName,
            submissionStatus: submission.status,
            taskStatus: submission.taskStatus,
            completedQuantity: submission.completedQuantity,
            quantity: submission.quantity
          });
          
          const taskStatus = computeProjectedStatus(submission);
          
          return {
            ...submission,
            taskStatus: taskStatus
          };
        });
        
        setPendingSubmissions(submissionsWithStatus);
        console.log(`Loaded ${submissionsWithStatus.length} pending submissions`);
      } else {
        console.error('Invalid submissions data format:', response.data);
        setPendingSubmissions([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pending submissions:', err);
      setError('Failed to load pending submissions. Please try again.');
      setPendingSubmissions([]);
      setLoading(false);
    }
  }, []);
  
  // Function to fetch all pending submissions
  const fetchAllPendingSubmissions = useCallback(() => {
    fetchSubmissions(null);
  }, [fetchSubmissions]);
  
  // Function to fetch pending submissions for a specific project
  const fetchPendingSubmissions = useCallback(() => {
    if (!projectId) {
      fetchAllPendingSubmissions();
      return;
    }
    fetchSubmissions(projectId);
  }, [projectId, fetchAllPendingSubmissions, fetchSubmissions]);
  
  // Fetch pending submissions when project is selected
  useEffect(() => {
    if (projectId === "") {
      // If no project is selected (showing all projects), fetch all pending submissions
      fetchAllPendingSubmissions();
    } else {
      // If a specific project is selected, fetch submissions for that project
      fetchPendingSubmissions();
    }
  }, [projectId, fetchPendingSubmissions, fetchAllPendingSubmissions]);
  
  // Initial load of all pending submissions after projects are loaded
  useEffect(() => {
    if (projects.length > 0) {
      fetchAllPendingSubmissions();
    }
  }, [projects, fetchAllPendingSubmissions]);
  
  // Handle project selection change
  const handleProjectChange = (e) => {
    setProjectId(e.target.value);
  };

  const handleResetFilters = () => {
    setProjectId('');
    setBuildingFilter('all');
    setTaskStatusFilter('all');
    setSubmitterFilter('all');
  };
  
  // Handle approve submission
  const handleApprove = async (submissionId) => {
    try {
      setLoading(true);
      
      console.log(`Approving submission ${submissionId} with payload:`, { status: 'approved' });
      
      // Use direct fetch with credentials to avoid CORS issues
      const response = await fetch(`${api.defaults.baseURL}/progress-submissions/${submissionId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: 'approved' }),
        credentials: 'include'
      });
      
      if (response.status === 401) {
        // fetch() bypasses the axios interceptor — route an expired session
        // through the same central logout + redirect handler.
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Approval response:', data);
      
      setSuccess('Progress submission approved successfully!');
      
      // Refresh pending submissions
      fetchPendingSubmissions();
      
      // Trigger Gantt chart refresh
      window.dispatchEvent(new CustomEvent('FORCE_GANTT_REFRESH', { 
        detail: { projectId: parseInt(projectId) } 
      }));
      
      setLoading(false);
    } catch (err) {
      console.error('Error approving submission:', err);
      setError(err.message || 'Failed to approve submission. Please try again.');
      setLoading(false);
    }
  };
  

  const getSubconDisplayName = (submission) => {
    if (!submission) return t('TIDAK DITENTUKAN');
    const subconName =
      submission.subconName ||
      submission.subcon?.name ||
      submission.subcontractorName ||
      submission.installationTeamName ||
      submission.task?.subcontractor?.name ||
      submission.task?.subcon?.name;
    return subconName ? `${subconName}`.toUpperCase() : t('TIDAK DITENTUKAN');
  };
  
  // Handle open rejection dialog
  const handleOpenRejectDialog = (submissionId) => {
    setRejectionDialog({
      open: true,
      submissionId,
      reason: ''
    });
  };
  
  // Handle close rejection dialog
  const handleCloseRejectDialog = () => {
    setRejectionDialog({
      ...rejectionDialog,
      open: false
    });
  };
  
  // Handle rejection reason change
  const handleReasonChange = (e) => {
    setRejectionDialog({
      ...rejectionDialog,
      reason: e.target.value
    });
  };
  
  // Handle reject submission
  const handleReject = async () => {
    if (!rejectionDialog.reason) {
      setError('Please provide a reason for rejection.');
      return;
    }
    
    try {
      setLoading(true);
      
      await api.patch(`/progress-submissions/${rejectionDialog.submissionId}/status`, {
        status: 'rejected',
        rejectionReason: rejectionDialog.reason
      });
      
      setSuccess('Progress submission rejected successfully.');
      
      // Close dialog
      handleCloseRejectDialog();
      
      // Refresh pending submissions
      fetchPendingSubmissions();
      
      setLoading(false);
    } catch (err) {
      console.error('Error rejecting submission:', err);
      setError(err.response?.data?.message || t('GAGAL MENOLAK PENGAJUAN. SILAKAN COBA LAGI.'));
      setLoading(false);
    }
  };
  
  // Handle open image preview
  const handleOpenImagePreview = (url, caption) => {
    // If URL is relative, make it absolute
    const fullUrl = url?.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || ''}${url}`;
    
    setImagePreview({
      open: true,
      url: fullUrl,
      caption: caption || t('FOTO PROGRES')
    });
  };
  
  // Handle open submission detail dialog
  const handleOpenDetailDialog = (submission) => {
    setDetailDialog({
      open: true,
      submission
    });
  };
  
  // Handle close submission detail dialog
  const handleCloseDetailDialog = () => {
    setDetailDialog({
      ...detailDialog,
      open: false
    });
  };
  
  // Handle close image preview
  const handleCloseImagePreview = () => {
    setImagePreview({
      ...imagePreview,
      open: false
    });
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setError(null);
    setSuccess(null);
  };

  const formatNumber = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0';
    return num.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTaskStatusInfo = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'to_do') return { label: t('BELUM MULAI'), color: 'default' };
    if (normalized === 'partial' || normalized === 'in_progress') return { label: t('PROGRES PARSIAL'), color: 'info' };
    if (normalized === 'done') return { label: t('100% - SELESAI'), color: 'success' };
    return { label: t('TIDAK DIKETAHUI'), color: 'default' };
  };

  const getProgressSnapshot = (submission) => {
    const total = Number(submission?.quantity || 0);
    const completedNow = Number(submission?.completedQuantity || 0);
    const submittedVolume = Number(submission?.volume || 0);

    const safeTotal = Number.isFinite(total) ? Math.max(total, 0) : 0;
    const safeCompleted = Number.isFinite(completedNow) ? Math.max(completedNow, 0) : 0;
    const safeSubmitted = Number.isFinite(submittedVolume) ? Math.max(submittedVolume, 0) : 0;

    const remaining = Math.max(safeTotal - safeCompleted, 0);
    const estimatedDone = Math.min(safeCompleted + safeSubmitted, safeTotal || safeCompleted + safeSubmitted);
    const estimatedRemaining = Math.max(safeTotal - estimatedDone, 0);

    const currentPercent = safeTotal > 0 ? Math.min((safeCompleted / safeTotal) * 100, 100) : 0;
    const estimatedPercent = safeTotal > 0 ? Math.min((estimatedDone / safeTotal) * 100, 100) : 0;

    return {
      total: safeTotal,
      remaining,
      completed: safeCompleted,
      submitted: safeSubmitted,
      estimatedDone,
      estimatedRemaining,
      estimatedPercent,
      currentPercent
    };
  };
  
  const filteredSubmissions = useMemo(() => {
    if (!Array.isArray(pendingSubmissions)) return [];

    return pendingSubmissions.filter((submission) => {
      if (!submission) return false;

      const buildingName = (
        submission.modularUnitName ||
        submission.buildingName ||
        submission.unitBuildingName ||
        submission.unitName ||
        ''
      ).trim();

      if (buildingFilter !== 'all' && buildingName !== buildingFilter) {
        return false;
      }

      if (taskStatusFilter !== 'all' && submission.taskStatus !== taskStatusFilter) {
        return false;
      }

      if (submitterFilter !== 'all' && (submission.submitterName || '').trim() !== submitterFilter) {
        return false;
      }

      return true;
    });
  }, [
    pendingSubmissions,
    buildingFilter,
    taskStatusFilter,
    submitterFilter,
  ]);

  const buildingOptions = useMemo(() => {
    const buildings = new Set();
    (pendingSubmissions || []).forEach((submission) => {
      const buildingName = (
        submission?.modularUnitName ||
        submission?.buildingName ||
        submission?.unitBuildingName ||
        submission?.unitName ||
        ''
      ).trim();
      if (buildingName) {
        buildings.add(buildingName);
      }
    });
    return Array.from(buildings).sort((a, b) => a.localeCompare(b));
  }, [pendingSubmissions]);

  const submitterOptions = useMemo(() => {
    const names = new Set();
    (pendingSubmissions || []).forEach((submission) => {
      if (submission?.submitterName) {
        names.add(submission.submitterName.trim());
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [pendingSubmissions]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (projectId) count += 1;
    if (buildingFilter !== 'all') count += 1;
    if (taskStatusFilter !== 'all') count += 1;
    if (submitterFilter !== 'all') count += 1;
    return count;
  }, [projectId, buildingFilter, taskStatusFilter, submitterFilter]);
  const hasActiveFilters = activeFilterCount > 0;
  
  // Use effect to fetch all pending submissions on mount
  useEffect(() => {
    fetchAllPendingSubmissions();
  }, [fetchAllPendingSubmissions]);
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with blue gradient */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a, #1e3a5f, #1e40af)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.2), transparent 70%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" position="relative" zIndex={1}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', mb: 1 }}>
              {t('PERSETUJUAN LAPORAN PROGRES')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {t('Kelola persetujuan laporan progres pekerjaan proyek.')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              icon={<FilterAltIcon />}
              label={`${activeFilterCount} ${t('FILTER AKTIF')}`}
              color={hasActiveFilters ? 'primary' : 'default'}
              variant={hasActiveFilters ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 700,
                bgcolor: hasActiveFilters ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }}
            />
            <Button
              variant="contained"
              onClick={fetchAllPendingSubmissions}
              disabled={loading}
              startIcon={<RefreshIcon />}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                fontWeight: 700,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              {t('SEGARKAN')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              disabled={loading || !hasActiveFilters}
              startIcon={<RestartAltIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                fontWeight: 700,
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {t('RESET FILTER')}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' } }}
      >
        <Tab label={t('MENUNGGU APPROVAL')} />
        <Tab label={t('SUDAH DI-APPROVE')} />
      </Tabs>

      {activeTab === 1 && <ApprovedProgressList projects={projects} />}

      {activeTab === 0 && (
      <>
      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#FFFFFF',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
          {t('FILTER DATA')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <FormControl variant="outlined" size="small" sx={{ width: '100%', minWidth: '260px' }}>
              <InputLabel>{t('FILTER PROYEK')}</InputLabel>
              <Select
                value={projectId || ''}
                onChange={handleProjectChange}
                disabled={loading}
                label={t('FILTER PROYEK')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 'auto',
                      minWidth: '200px'
                    },
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    paddingRight: '32px !important',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              >
                <MenuItem value="">
                  <Typography noWrap>{t('SEMUA PROYEK')}</Typography>
                </MenuItem>
                {Array.isArray(projects) && projects.length > 0 ? (
                  projects.map(project => (
                    <MenuItem key={project.id} value={project.id} sx={{ maxWidth: '100%' }}>
                      <Typography noWrap>{project.name}</Typography>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{t('TIDAK ADA PROYEK')}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{t('FILTER BANGUNAN')}</InputLabel>
              <Select
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e.target.value)}
                label={t('FILTER BANGUNAN')}
              >
                <MenuItem value="all">{t('SEMUA BANGUNAN')}</MenuItem>
                {buildingOptions.map((building) => (
                  <MenuItem key={building} value={building}>{building}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{t('STATUS TUGAS')}</InputLabel>
              <Select
                value={taskStatusFilter}
                onChange={(e) => setTaskStatusFilter(e.target.value)}
                label={t('STATUS TUGAS')}
              >
                <MenuItem value="all">{t('SEMUA STATUS')}</MenuItem>
                <MenuItem value="partial">{t('PROGRES PARSIAL')}</MenuItem>
                <MenuItem value="done">{t('100% - SELESAI')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{t('DIAJUKAN OLEH')}</InputLabel>
              <Select
                value={submitterFilter}
                onChange={(e) => setSubmitterFilter(e.target.value)}
                label={t('DIAJUKAN OLEH')}
              >
                <MenuItem value="all">{t('SEMUA PENGAJU')}</MenuItem>
                {submitterOptions.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#FFFFFF',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
        }}
      >
          
          {pendingSubmissions.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Chip 
                label={`${filteredSubmissions.length} / ${pendingSubmissions.length} ${t('LAPORAN MENUNGGU PERSETUJUAN')}`} 
                color="primary" 
                sx={{ fontWeight: 'bold', mb: 2 }} 
              />
              
              <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 2 }}>
                <Table>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell width="82%"><strong>{t('RINGKASAN LAPORAN')}</strong></TableCell>
                      <TableCell width="18%" align="center"><strong>{t('AKSI')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubmissions.length > 0 ? filteredSubmissions.map((submission) => {
                      const statusInfo = getTaskStatusInfo(submission.taskStatus);
                      const snapshot = getProgressSnapshot(submission);

                      return (
                        <TableRow key={submission.id} hover>
                          <TableCell>
                            <Box
                              sx={{
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                backgroundColor: '#f8fafc'
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1.1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                  backgroundColor: '#ffffff',
                                  mb: 1.1
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                                    gap: 1.2
                                  }}
                                >
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      {t('PROYEK')}
                                    </Typography>
                                    <Tooltip title={submission.projectName || '-'}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          lineHeight: 1.35,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden'
                                        }}
                                      >
                                        {submission.projectName || '-'}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      {t('BANGUNAN')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {submission.modularUnitName || '-'}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      {t('KATEGORI')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {submission.workCategoryName || '-'}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      {t('TUGAS')}
                                    </Typography>
                                    <Tooltip title={submission.taskName || '-'}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          lineHeight: 1.35,
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {submission.taskName || '-'}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                  gap: 1.2
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {t('DIAJUKAN OLEH')}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#3f51b5' }}>
                                      {submission.submitterName?.charAt(0) || 'U'}
                                    </Avatar>
                                    <Typography variant="body2">{submission.submitterName || '-'}</Typography>
                                  </Box>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {t('INPUT PROGRES')}
                                  </Typography>
                                  <Typography variant="body2">
                                    {submission.createdAt ? format(new Date(submission.createdAt), 'dd MMM yyyy') : '-'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {submission.createdAt ? format(new Date(submission.createdAt), 'HH:mm') : ''}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {t('TANGGAL PEKERJAAN')}
                                  </Typography>
                                  <Typography variant="body2">
                                    {submission.progressWorkDate ? format(new Date(submission.progressWorkDate), 'dd MMM yyyy') : '-'}
                                  </Typography>
                                </Box>
                              </Box>

                              <Divider sx={{ my: 1.2 }} />

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  {t('RINGKASAN PROGRES')}
                                </Typography>
                                <Chip size="small" label={statusInfo.label} color={statusInfo.color} />
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={snapshot.estimatedPercent}
                                sx={{
                                  height: 8,
                                  borderRadius: 10,
                                  mb: 1.25,
                                  backgroundColor: '#dbe5f0',
                                  '& .MuiLinearProgress-bar': { borderRadius: 10, backgroundColor: '#7ba7d6' }
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {t('SAAT INI')}: <strong>{snapshot.currentPercent.toFixed(2)}%</strong> • {t('ESTIMASI SETELAH UPDATE')}: <strong>{snapshot.estimatedPercent.toFixed(2)}%</strong>
                              </Typography>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                                  gap: 0.75,
                                  mt: 1
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" color="text.secondary">{t('TOTAL TARGET')}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(snapshot.total)} {submission.unit || 'm³'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">{t('SELESAI SAAT INI')}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(snapshot.completed)} {submission.unit || 'm³'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">{t('VOLUME DIAJUKAN')}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(snapshot.submitted)} {submission.unit || 'm³'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">{t('ESTIMASI SELESAI (SETELAH APPROVE)')}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(snapshot.estimatedDone)} {submission.unit || 'm³'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">{t('ESTIMASI SISA (SETELAH APPROVE)')}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(snapshot.estimatedRemaining)} {submission.unit || 'm³'}</Typography>
                                </Box>
                              </Box>
                              {submission.area && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                  {t('AREA')}: {submission.area} m²
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <Tooltip title={t('LIHAT DETAIL')}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenDetailDialog(submission)}
                                  sx={{ width: 118 }}
                                >
                                  <VisibilityIcon sx={{ mr: 0.5 }} /> {t('DETAIL')}
                                </Button>
                              </Tooltip>
                              
                              <Tooltip title={t('SETUJUI')}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleApprove(submission.id)}
                                  disabled={loading}
                                  sx={{ width: 118 }}
                                >
                                  <CheckCircleIcon sx={{ mr: 0.5 }} /> {t('SETUJUI')}
                                </Button>
                              </Tooltip>
                              <Tooltip title={t('TOLAK')}>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => handleOpenRejectDialog(submission.id)}
                                  disabled={loading}
                                  sx={{ width: 118 }}
                                >
                                  <CancelIcon sx={{ mr: 0.5 }} /> {t('TOLAK')}
                                </Button>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          {t('TIDAK ADA LAPORAN SESUAI FILTER')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : !loading && (
            <Alert severity="info" sx={{ mt: 3 }}>
              {t('TIDAK ADA LAPORAN PROGRES YANG MENUNGGU PERSETUJUAN SAAT INI.')}
            </Alert>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress sx={{ color: '#3B82F6' }} />
            </Box>
          )}
        </Paper>
      </>
      )}

        {/* Rejection Dialog */}
        <Dialog open={rejectionDialog.open} onClose={handleCloseRejectDialog}>
          <DialogTitle>{t('TOLAK LAPORAN PROGRES')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('HARAP BERIKAN ALASAN PENOLAKAN LAPORAN PROGRES INI:')}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label={t('ALASAN PENOLAKAN')}
              type="text"
              fullWidth
              multiline
              rows={3}
              value={rejectionDialog.reason}
              onChange={handleReasonChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRejectDialog} color="primary">
              {t('BATAL')}
            </Button>
            <Button 
              onClick={handleReject} 
              color="error" 
              variant="contained"
              disabled={!rejectionDialog.reason}
            >
              {t('TOLAK LAPORAN')}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Image Preview Dialog */}
        <Dialog 
          open={imagePreview.open} 
          onClose={handleCloseImagePreview}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{imagePreview.caption}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <img 
                src={imagePreview.url} 
                alt={imagePreview.caption}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseImagePreview} color="primary">
              {t('TUTUP')}
            </Button>
            <Button 
              component="a" 
              href={imagePreview.url} 
              target="_blank" 
              rel="noopener noreferrer"
              color="primary"
              variant="contained"
            >
              {t('BUKA DI TAB BARU')}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Submission Detail Dialog */}
        <Dialog
          open={detailDialog.open}
          onClose={handleCloseDetailDialog}
          maxWidth="lg"
          fullWidth
        >
          {detailDialog.submission && (() => {
            const submission = detailDialog.submission;
            const statusInfo = getTaskStatusInfo(submission.taskStatus);
            const snapshot = getProgressSnapshot(submission);
            const unitLabel = submission.unit || 'm³';
            const attachments = Array.isArray(submission.attachments) ? submission.attachments : [];

            return (
              <>
                <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {t('DETAIL LAPORAN PROGRES')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {submission.projectName || '-'} • {submission.taskName || '-'}
                      </Typography>
                    </Box>
                    <Chip size="small" label={statusInfo.label} color={statusInfo.color} />
                  </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('INFORMASI UTAMA')}
                        </Typography>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('PROYEK')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{submission.projectName || '-'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('BANGUNAN')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{submission.modularUnitName || '-'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('KATEGORI')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{submission.workCategoryName || '-'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('TUGAS')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {submission.taskName || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('DIAJUKAN OLEH')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#3f51b5' }}>
                                {submission.submitterName?.charAt(0) || 'U'}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{submission.submitterName || '-'}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('SUBCON / INSTALLATION TEAM')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{getSubconDisplayName(submission)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('TANGGAL SUBMIT')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {submission.createdAt ? format(new Date(submission.createdAt), 'dd MMMM yyyy HH:mm') : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">{t('TANGGAL PEKERJAAN')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {submission.progressWorkDate ? format(new Date(submission.progressWorkDate), 'dd MMMM yyyy') : '-'}
                            </Typography>
                          </Grid>
                          {submission.area && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">{t('AREA')}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{submission.area} m²</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('RINGKASAN PROGRES')}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={snapshot.estimatedPercent}
                          sx={{
                            height: 10,
                            borderRadius: 10,
                            mb: 1.25,
                            backgroundColor: '#dbe5f0',
                            '& .MuiLinearProgress-bar': { borderRadius: 10, backgroundColor: '#7ba7d6' }
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {t('SAAT INI')}: <strong>{snapshot.currentPercent.toFixed(2)}%</strong> • {t('ESTIMASI SETELAH UPDATE')}: <strong>{snapshot.estimatedPercent.toFixed(2)}%</strong>
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                            gap: 1.2
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('TOTAL TARGET')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(snapshot.total)} {unitLabel}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('SELESAI SAAT INI')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(snapshot.completed)} {unitLabel}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('VOLUME DIAJUKAN')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(snapshot.submitted)} {unitLabel}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('ESTIMASI SELESAI (SETELAH APPROVE)')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(snapshot.estimatedDone)} {unitLabel}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('ESTIMASI SISA (SETELAH APPROVE)')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(snapshot.estimatedRemaining)} {unitLabel}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    {submission.notes && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            {t('CATATAN')}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {submission.notes}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('BUKTI PEKERJAAN')} {attachments.length > 0 ? `(${attachments.length} ${t('FILE')})` : ''}
                        </Typography>

                        {attachments.length > 0 ? (
                          <Grid container spacing={2}>
                            {attachments.map((attachment, index) => {
                              const fileUrl = attachment.fileUrl?.startsWith('http')
                                ? attachment.fileUrl
                                : `${process.env.REACT_APP_API_URL || ''}${attachment.fileUrl}`;
                              const isImage = attachment.fileType?.startsWith('image/');
                              const isPdf = attachment.fileType === 'application/pdf' || attachment.fileName?.endsWith('.pdf');
                              const isDoc = attachment.fileType?.includes('word') || attachment.fileName?.endsWith('.doc') || attachment.fileName?.endsWith('.docx');

                              return (
                                <Grid item xs={12} sm={6} md={4} key={attachment.id || index}>
                                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                      {isImage ? (
                                        <Box sx={{ height: 180, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                          <img
                                            src={fileUrl}
                                            alt={`Bukti Progress ${index + 1}`}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                                            onClick={() => handleOpenImagePreview(fileUrl, `Bukti Progress ${index + 1}`)}
                                          />
                                        </Box>
                                      ) : isPdf ? (
                                        <Box sx={{ height: 180, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                          <PictureAsPdfIcon sx={{ fontSize: 64, color: '#f44336' }} />
                                        </Box>
                                      ) : isDoc ? (
                                        <Box sx={{ height: 180, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                          <DescriptionIcon sx={{ fontSize: 64, color: '#2196f3' }} />
                                        </Box>
                                      ) : (
                                        <Box sx={{ height: 180, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                          <InsertDriveFileIcon sx={{ fontSize: 64, color: '#757575' }} />
                                        </Box>
                                      )}
                                      <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 600 }}>
                                        {attachment.fileName || `${t('FILE')} ${index + 1}`}
                                      </Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'center', p: 1.5, pt: 0 }}>
                                      {isImage && (
                                        <Button
                                          size="small"
                                          startIcon={<VisibilityIcon />}
                                          onClick={() => handleOpenImagePreview(fileUrl, `Bukti Progress ${index + 1}`)}
                                        >
                                          {t('LIHAT')}
                                        </Button>
                                      )}
                                      <Button
                                        component="a"
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        download
                                      >
                                        {t('DOWNLOAD')}
                                      </Button>
                                    </CardActions>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        ) : submission.attachmentUrl ? (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                              <img
                                src={submission.attachmentUrl?.startsWith('http')
                                  ? submission.attachmentUrl
                                  : `${process.env.REACT_APP_API_URL || ''}${submission.attachmentUrl}`}
                                alt="Bukti Progress"
                                style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain', cursor: 'pointer' }}
                                onClick={() => handleOpenImagePreview(
                                  submission.attachmentUrl?.startsWith('http')
                                    ? submission.attachmentUrl
                                    : `${process.env.REACT_APP_API_URL || ''}${submission.attachmentUrl}`,
                                  `Bukti Progress - ${submission.taskName}`
                                )}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 1 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleOpenImagePreview(
                                  submission.attachmentUrl?.startsWith('http')
                                    ? submission.attachmentUrl
                                    : `${process.env.REACT_APP_API_URL || ''}${submission.attachmentUrl}`,
                                  `Bukti Progress - ${submission.taskName}`
                                )}
                              >
                                {t('LIHAT')}
                              </Button>
                              <Button
                                component="a"
                                href={submission.attachmentUrl?.startsWith('http')
                                  ? submission.attachmentUrl
                                  : `${process.env.REACT_APP_API_URL || ''}${submission.attachmentUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                color="primary"
                                download
                              >
                                {t('DOWNLOAD')}
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            {t('TIDAK ADA BUKTI PEKERJAAN YANG DILAMPIRKAN')}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 1.5, justifyContent: 'space-between' }}>
                  <Button onClick={handleCloseDetailDialog} color="inherit">
                    {t('TUTUP')}
                  </Button>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      onClick={() => {
                        handleOpenRejectDialog(submission.id);
                        handleCloseDetailDialog();
                      }}
                      color="error"
                      variant="outlined"
                      startIcon={<CancelIcon />}
                    >
                      {t('TOLAK')}
                    </Button>
                    <Button
                      onClick={() => {
                        handleApprove(submission.id);
                        handleCloseDetailDialog();
                      }}
                      color="success"
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                    >
                      {t('SETUJUI')}
                    </Button>
                  </Box>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>
        
        
        {/* Snackbars for error and success messages */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        
        <Snackbar 
          open={!!success} 
          autoHideDuration={6000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default ApprovalProgress;
